import React from "react";
import { useAppSelector } from "store";

import {
    Box,
    Card,
    CardContent,
    Collapse,
    Divider,
    Grid,
    Icon,
    Typography,
    useTheme
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
//@ts-ignore
import StopIcon from "@material-ui/icons/Stop";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecordRounded";
import PlayArrowIcon from '@material-ui/icons/PlayArrow';

import OtherStoreIconSvg from "assets/images/other-store-icon.svg";
import useColourPalette from "components/visuals/useColourPalette";

import { selectPlaceCategories, selectSizeBuckets } from "modules/customer/tools/location/locationSlice";
import { PinnedLocation } from "modules/customer/tools/location/pinnedLocation";
import mathUtils from "utils/mathUtils";
import numberFormatter from "utils/numberFormatter";
import { NearbyStore } from "modules/customer/tools/location/retailers/nearbyStore";

const StyledCard = withStyles(theme => ({
    root: {
        position: "absolute",
        zIndex: theme.zIndex.drawer,
        margin: theme.spacing(2),
        bottom: theme.spacing(2),
        width: theme.spacing(34),
        // @ts-ignore
        backgroundColor: theme.palette.background.insight
    }
}))(Card);

const StyledCardContent = withStyles(theme => ({
    root: {
        "&:last-child": {
            padding: theme.spacing(2)
        }
    }
}))(CardContent);

const StyledDivider = withStyles(theme => ({
    root: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        // @ts-ignore
        backgroundColor: theme.palette.quaternary.light
    }
}))(Divider);

interface LegendProps {
    nearbyStore?: NearbyStore,
    pinnedLocation?: PinnedLocation;
}

const Legend: React.FC<LegendProps> = (props) => {
    const { nearbyStore, pinnedLocation } = props;
    const theme = useTheme();
    const colourPalette = useColourPalette();
    const placeCategories = useAppSelector(selectPlaceCategories);
    const sizeBuckets = useAppSelector(selectSizeBuckets);
    const competitorCategoryName = placeCategories.find(pc => pc.id === nearbyStore?.placeCategoryId)?.name ?? "Unknown";
    const sizeBucket = sizeBuckets.find(sb => sb.id === nearbyStore?.sizeBucketId);
    const sizeBucketName = sizeBucket?.label ?? "Unknown";
    const sizeRange = sizeBucket?.sizeRange;
    const sizeLabel = sizeRange ? `${sizeBucketName} (${sizeRange})` : sizeBucketName;
    let competitorColour = colourPalette.categorical[0];
    switch (nearbyStore?.brandClassification.name) {
        case "Luxury":
            competitorColour = colourPalette.categorical[1];
            break;
        case "Premium":
            competitorColour = colourPalette.categorical[0];
            break;
        case "Mainstream":
            competitorColour = colourPalette.categorical[5];
            break;
        case "Value":
            competitorColour = colourPalette.categorical[6];
            break;
        case "Budget":
            competitorColour = "#FFE557";
            break;
        case "Unclassified":
            competitorColour = theme.palette.text.secondary;
            break;
        default:
            competitorColour = theme.palette.text.secondary;
            break;
    }

    const iconStyle = (color: string, rotation?: number, borderColour?: string) => ({
        color,
        cursor: "pointer",
        ...(rotation ? { transform: `rotate(${rotation}deg)` } : {}),
        ...(borderColour ? {
            stroke: borderColour,
            strokeWidth: 2
        } : {})
    });

    const iconContainerStyle = (addPadding?: boolean) => ({
        ...(addPadding ? { paddingLeft: theme.spacing(0.25) } : {}),
        display: "flex",
        alignItems: "center",
    });

    return (
        <StyledCard elevation={0}>
            <StyledCardContent>
                <Collapse in={nearbyStore !== undefined}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div">
                                {nearbyStore?.storeName}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div" style={{ color: competitorColour, fontWeight: "bold" }} align="right">
                                {nearbyStore?.brandClassification.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" component="div">
                                Store category
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {competitorCategoryName}
                            </Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant="body2" component="div">
                                Distance from selected store
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {`${numberFormatter.toDecimalPlaces(
                                    mathUtils.haversineDistance(
                                        pinnedLocation?.latitude ?? 0,
                                        pinnedLocation?.longitude ?? 0,
                                        nearbyStore?.latitude ?? 0,
                                        nearbyStore?.longitude ?? 0
                                    ) ?? 0, 1)
                                    }km`
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="body2" component="div">
                                Store size
                            </Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {sizeLabel}
                            </Typography>
                        </Grid>
                    </Grid>
                    <StyledDivider />
                </Collapse>
                <Box display="flex" style={{ paddingTop: theme.spacing(0.25) }}>
                    <Icon fontSize="small">
                        <img src={OtherStoreIconSvg} height={24} width={24} alt="portfolio-competition-nearby-competition-legend-other-store-icon" />
                    </Icon>
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.5), paddingLeft: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Selected location
                    </Typography>
                </Box>
                <Box style={iconContainerStyle(true)}>
                    <StopIcon fontSize="small" cursor="pointer" style={iconStyle(colourPalette.categorical[1], 45,)} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Luxury retailer
                    </Typography>
                </Box>
                <Box style={iconContainerStyle()}>
                    <PlayArrowIcon fontSize="medium" cursor="pointer" style={iconStyle(colourPalette.categorical[0], 270)} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Premium retailer
                    </Typography>
                </Box>
                <Box style={iconContainerStyle(true)}>
                    <StopIcon fontSize="small" cursor="pointer" style={iconStyle(colourPalette.categorical[5])} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Mainstream retailer
                    </Typography>
                </Box>
                <Box style={iconContainerStyle()}>
                    <PlayArrowIcon fontSize="medium" cursor="pointer" style={iconStyle(colourPalette.categorical[6], 90)} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Value retailer
                    </Typography>
                </Box>
                <Box style={iconContainerStyle(true)}>
                    <FiberManualRecordIcon fontSize="small" cursor="pointer" style={{ ...iconStyle(colourPalette.categorical[4]), transform: "scale(0.85)" }} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Budget retailer
                    </Typography>
                </Box>
                <Box style={iconContainerStyle(true)}>
                    <StopIcon fontSize="small" cursor="pointer" style={{ ...iconStyle("#000000", 0, "#9C9AA6"), transform: "scale(0.9)" }} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Unclassified retailer
                        <br />
                        &nbsp;&nbsp;(likely an independent)
                    </Typography>
                </Box>
            </StyledCardContent>
        </StyledCard>
    );
};

export default Legend;
